import { useCallback, useEffect, useState } from "react";
import { useCurrentDatabaseManager } from "./useDatabases";
import _ from "lodash";
import { DatabaseIdentifier } from "./manager";

/**
 * Get some status about the current database and control synchronisation
 */
export function useDatabaseStatus() {
  const mngr = useCurrentDatabaseManager();
  const [syncing, setSyncing] = useState(false);
  const [syncingInventories, setSyncingInventories] = useState<string[]>([]);
  const [dirty, setDirty] = useState<DatabaseIdentifier[]>([]);
  const [lastSync, setLastSync] = useState<Partial<Record<DatabaseIdentifier, Date>>>({});
  const [syncError, setSyncError] = useState<unknown | null>(null);
  const [loadedInventories, setLoadedInventories] = useState<string[]>([]);

  const handleInventoryAdd = useCallback(() => {
    setLoadedInventories(mngr?.loadedInventories || []);
  }, [mngr]);
  const handleSyncStart = useCallback((event: { detail: { inventories: string[] } }) => {
    setSyncing(true);
    setSyncingInventories(event.detail.inventories);
    setSyncError(null);
  }, []);
  const handleSyncError = useCallback((event: { detail: { inventories: string[]; error: unknown } }) => {
    setSyncError(event.detail.error);
  }, []);
  const handleSyncFinished = useCallback(() => {
    setSyncing(false);
    setSyncingInventories([]);

    const manager = mngr!;
    setLastSync(manager.lastSync);
  }, [mngr]);
  const handleDirtySet = useCallback((event: { detail: { database: DatabaseIdentifier } }) => {
    setDirty((d) => _.uniq([...d, event.detail.database]));
  }, []);
  const handleDirtyClear = useCallback((event: { detail: { database: DatabaseIdentifier } }) => {
    setDirty((i) => (i.includes(event.detail.database) ? i.filter((id_) => id_ !== event.detail.database) : i));
  }, []);

  useEffect(() => {
    const manager = mngr!;
    setLoadedInventories(manager.loadedInventories);
    setSyncing(manager.syncingInventories !== null);
    setSyncingInventories(manager.syncingInventories || []);
    setSyncError(manager.lastSyncError);
    setDirty(manager.dirty);
    setLastSync(manager.lastSync);
    manager.on("inventory:add", handleInventoryAdd);
    manager.on("sync:start", handleSyncStart);
    manager.on("sync:error", handleSyncError);
    manager.on("sync:finished", handleSyncFinished);
    manager.on("dirty:set", handleDirtySet);
    manager.on("dirty:clear", handleDirtyClear);
    return () => {
      manager.off("inventory:add", handleInventoryAdd);
      manager.off("sync:start", handleSyncStart);
      manager.off("sync:error", handleSyncError);
      manager.off("sync:finished", handleSyncFinished);
      manager.off("dirty:set", handleDirtySet);
      manager.off("dirty:clear", handleDirtyClear);
    };
  }, [mngr, handleSyncStart, handleSyncError, handleInventoryAdd, handleSyncFinished, handleDirtySet, handleDirtyClear]);

  const sync = useCallback(
    (inventories: string[]) => {
      if (mngr) {
        return mngr.sync(inventories);
      } else {
        return Promise.resolve();
      }
    },
    [mngr]
  );

  return {
    loadedInventories,
    syncing,
    syncingInventories,
    syncError,
    dirty: dirty.some((db) => !db.startsWith("inventory")),
    dirtyInventories: _.uniq(dirty.filter((db) => db.startsWith("inventory")).map((db) => db.split(":")[1])),
    lastSync: _.min(
      Object.entries(lastSync)
        .filter(([key, _]) => !key.startsWith("inventory"))
        .map(([_, value]) => value)
    ),
    lastSyncInventories: _.mapValues(
      _.groupBy(
        Object.entries(lastSync)
          .filter(([key, _]) => key.startsWith("inventory"))
          .map<[string, Date]>(([key, value]) => [key.split(":")[1], value!]),
        (v) => v[0]
      ),
      (v) => _.min(v.map(([_, v]) => v))
    ),
    sync,
  };
}
